/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import handleTransformX from "../../utils/transformX";
import { ConversionButtonProps } from "../../types/types";
import { Button } from "@material-tailwind/react";
import { useSmartWalletSigner } from "../../hooks/useSmartWalletSigner";

const ConversionButton: React.FC<ConversionButtonProps> = ({
  state,
  setState,
  conversionRatio,
  loading,
  balanceNAU,
  amountNAU,
  setShowModal,
  onTransactionSuccess,
  setModalTx,
}) => {
  // Track conversion process state
  const [isProcessing, setIsProcessing] = useState(false);
  // Get signer and connection status
  const {
    signer,
    isConnected,
    loading: walletLoading,
    error,
  } = useSmartWalletSigner();

  // Parse NAU balance and calculate remaining after conversion amount
  const parsedBalance = parseFloat(balanceNAU);
  const remainingBalance = parsedBalance - amountNAU;

  // Determine if button should be disabled
  const isDisabled =
    loading ||
    isProcessing ||
    walletLoading ||
    !isConnected ||
    parsedBalance <= 0 ||
    remainingBalance < 0 ||
    !signer ||
    Boolean(error);

  const handleClick = async () => {
    if (isDisabled || !signer) return;

    try {
      setIsProcessing(true);
      setState((prev) => ({ ...prev, txStatus: "pending" }));
      setModalTx({ txStatus: "pending", txHash: null });
      setShowModal(true);

      // Perform the NAU to X transformation transaction
      await handleTransformX(
        signer,
        state,
        setState,
        conversionRatio,
        setShowModal,
        balanceNAU,
        setModalTx
      );

      // Callback on successful transaction
      onTransactionSuccess();
    } catch (err) {
      console.error("Error during conversion:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {/* Display wallet connection error */}
      {error && (
        <div className="mb-2 text-red-500 text-sm text-center">
          Wallet connection error: {error}
        </div>
      )}
      <p className="text-center text-gray-700 font-bold mb-2">
        Clicking Convert will burn your NAU and mint the selected amount of
        NAUY/NAUN. This action is irreversible.
      </p>
      <Button
        {...({
          onClick: handleClick,
          disabled: isDisabled,
          variant: "text",
          className:
            "w-full py-2 text-[12px] font-semibold text-white flex items-center  rounded-full justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-[var(--color-secondary-light)] via-[var(--color-primary)] to-[var(--color-primary-dark)] hover:brightness-110 transition ",
        } as any)}
      >
        {/* Show spinner when loading or processing */}
        {(loading || isProcessing || walletLoading) && (
          <svg
            className="animate-spin h-4 w-4 text-white motion-reduce:hidden"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8H4z"
            />
          </svg>
        )}
        {/* Toggle button text */}
        {loading || isProcessing || walletLoading ? "Processing..." : "Convert"}
      </Button>
    </>
  );
};

export default ConversionButton;
